import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';

import DeleteActionButton from '../../../../components/designe/ActionButtons/DeleteActionButton';
import EditActionButton from '../../../../components/designe/ActionButtons/EditActionButton';
import SortActionButton from '../../../../components/designe/ActionButtons/SortActionButton';
import AppImageGalleryContainer from '../../../../components/designe/app-image-gallery/AppImageGalleryContainer';
import { AppDashedBlockButton } from '../../../../components/designe/buttons/block-buttons';
import AppInfoTooltipButton from '../../../../components/designe/tooltips/AppInfoTooltipButton';
import ViewField from '../../../../components/reusable/view-field/ViewField';
import { useGetTrainingActivityTypes } from '../../../../redux/api/trainingsAPI';
import { IExerciseOption } from '../../../../types/interfaces/exercise-option';
import { ITraining } from '../../../../types/interfaces/training';
import { ITrainingSet } from '../../../../types/interfaces/training-set';
import { getTrainingDate } from '../../../../utils/datetime/date-time-utils';
import {
  formatTrainingCalories,
  getTrainingActivityTypeName,
  shouldShowTrainingCalories,
} from '../../../../utils/training';
import { getSetKey } from '../../../../utils/training/set-utils';
import { useStyles } from './FinishedTrainingSummary.styles';
import FinishedTrainingSetGroup from './finished-training-set-group/FinishedTrainingSetGroup';

export interface IFinishedTrainingSummaryProps {
  training: ITraining;
  trainingSetGroups: Record<number, ITrainingSet[]> | null;
  exerciseOptions: IExerciseOption[] | undefined;
  onEditClick: () => void;
  onDeleteClick: () => void;
  onSortClick: () => void;
  onEditSetGroupClick: (trainingSetGroup: ITrainingSet[]) => void;
  onAddTrainingSetClick: () => void;
}

export default function FinishedTrainingSummary({
  training,
  trainingSetGroups,
  exerciseOptions,
  onEditClick,
  onDeleteClick,
  onSortClick,
  onEditSetGroupClick,
  onAddTrainingSetClick,
}: IFinishedTrainingSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data: activityTypes = [] } = useGetTrainingActivityTypes();
  const activityTypeName = getTrainingActivityTypeName(training, activityTypes);

  const { classes } = useStyles(theme);

  return (
    <Box className={classes.summaryRoot}>
      <Box className={classes.headerWrapper}>
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            maxWidth="calc(100% - 40px)">
            <Typography variant="h1">{training?.title}</Typography>
            {training?.isGenerated && (
              <Typography variant="primary-wrapped-sm">
                {t('trainings.aiGenerated')}
              </Typography>
            )}
          </Box>
          {training?.permissions?.canEditInfo ||
          training?.permissions?.canDelete ||
          training?.permissions?.canEdit ? (
            <Box display="flex" gap={0.5} marginTop={0.5}>
              {training?.permissions?.canEdit ? (
                <SortActionButton onClick={onSortClick} />
              ) : null}
              {training?.permissions?.canEditInfo ? (
                <EditActionButton onClick={onEditClick} />
              ) : null}
              {training?.permissions?.canDelete ? (
                <DeleteActionButton onClick={onDeleteClick} />
              ) : null}
            </Box>
          ) : null}
        </Box>

        {training?.startDate && (
          <ViewField
            value={getTrainingDate(training)}
            label={t('trainings.trainingDate')}
          />
        )}

        {activityTypeName && (
          <ViewField
            value={activityTypeName}
            label={t('trainings.activityType')}
          />
        )}

        {!!training?.totalLoad && (
          <ViewField
            value={training?.totalLoad}
            label={t('trainings.totalLoad')}
          />
        )}

        {shouldShowTrainingCalories(training?.totalCalories) && (
          <ViewField
            value={formatTrainingCalories(training.totalCalories)}
            label={t('trainings.totalCalories')}
            tooltip={
              <AppInfoTooltipButton text={t('trainings.totalCaloriesHint')} />
            }
          />
        )}

        {training?.description && (
          <ViewField
            value={training.description}
            label={t('common.description')}
          />
        )}

        {training?.comment && (
          <ViewField value={training.comment} label={t('trainings.comment')} />
        )}

        {training?.files?.length > 0 && (
          <Box className={classes.fileContainer}>
            <Box maxWidth="500px">
              <AppImageGalleryContainer files={training.files} height={200} />
            </Box>
          </Box>
        )}
      </Box>

      {(training?.trainingSets?.length ?? 0) > 0 && !trainingSetGroups ? (
        <Box display="flex" justifyContent="center" padding={3}>
          <CircularProgress size={32} />
        </Box>
      ) : null}
      {trainingSetGroups && (
        <>
          <Box className={classes.trainingSetGroupsWrapper}>
            {Object.values(trainingSetGroups)?.map(
              (trainingSetGroup: ITrainingSet[]) => (
                <FinishedTrainingSetGroup
                  key={getSetKey(trainingSetGroup[0])}
                  trainingSetGroup={trainingSetGroup}
                  exerciseOptions={exerciseOptions}
                  trainingId={training?.id}
                  canEdit={!!training?.permissions?.canEdit}
                  onEditSetGroupClick={onEditSetGroupClick}
                />
              ),
            )}
          </Box>
          {training?.permissions?.canEdit && (
            <Box marginTop={2} marginBottom={2}>
              <AppDashedBlockButton
                fullWidth
                onClick={() => onAddTrainingSetClick()}>
                <Box display="flex" gap={1} alignItems="center">
                  <AddIcon />
                  {t('trainings.addExercise')}
                </Box>
              </AppDashedBlockButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
